import {HttpClient, HttpErrorResponse, HttpStatusCode} from '@angular/common/http';
import {AfterViewInit, Component, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {FormControl} from '@angular/forms';
import {MatInput} from '@angular/material/input';
import {MatSort} from '@angular/material/sort';
import {MatTableDataSource} from '@angular/material/table';
import {UserService} from 'auth-module/services/user.service';
import {BehaviorSubject, combineLatest, defer, iif, of, Subscription} from 'rxjs';
import {catchError, debounceTime, filter, map, startWith, switchMap, tap} from 'rxjs/operators';
import {environment} from '../../../../environments/environment';
import {SearchElement} from '../../../shared/model/search.element';
import {ReportService} from '../../../shared/services/report.service';

interface SearchResult {
  total: number;
  elem: SearchElement[];
}

@Component({
    selector: 'pm-search',
    templateUrl: './search.component.html',
    styleUrls: ['./search.component.css'],
    standalone: false
})
export class SearchComponent implements AfterViewInit, OnInit, AfterViewInit, OnDestroy {
  @ViewChild(MatSort, {static: true}) sort: MatSort;
  @ViewChild(MatInput) inputField: MatInput;

  private readonly PAGE_SIZE = 100;

  private pageSubject = new BehaviorSubject(0);
  private pagingEnd: boolean = false;
  private oldValues= {
    searchText: '',
    searchType: 'text',
    searchExact: false
  }
  folded = false;
  searchTextControl: FormControl<string> = new FormControl('');
  searchTypeControl: FormControl<string> = new FormControl('text');
  searchExactControl: FormControl<boolean> = new FormControl(false);

  searchResultsSubscription: Subscription;
  loading = false;
  displayedColumns = ['label', 'path'];
  dataSource = new MatTableDataSource<SearchElement>();
  activeSearchElement: any = null;
  protected helpIsOpen: boolean = false;

  constructor(private userService: UserService, private http: HttpClient, private reportService: ReportService) {
    this.dataSource.sortingDataAccessor = (data, sortHeaderId) => data[sortHeaderId]?.toLocaleLowerCase() ?? '';
  }

  ngOnInit() {
    this.searchResultsSubscription = combineLatest([this.searchTextControl.valueChanges.pipe(startWith('')),
      this.searchTypeControl.valueChanges.pipe(startWith('text')), this.searchExactControl.valueChanges.pipe(startWith(false)), this.pageSubject.pipe(startWith(0))])
      .pipe(filter(([searchString, _]) => searchString?.length >= 3), debounceTime(250),
        tap(() => this.loading = true), switchMap(([searchText, searchType, searchExact, page]) => {
          const url = environment.api + this.userService.domain + '/search';

          if(this.oldValues.searchText !== searchText || this.oldValues.searchType !== searchType || this.oldValues.searchExact !== searchExact) {
            page = 0;
            this.pageSubject.next(0);
            this.pagingEnd = false;
            this.oldValues = {searchText, searchType, searchExact}
          }

          return iif(() => !!searchText, defer(() => this.http.post<SearchResult>(url, {
            searchString: searchText,
            searchType: searchType,
            searchExact: searchExact,
            limit: this.PAGE_SIZE,
            offset: this.PAGE_SIZE * page
          })).pipe(catchError(err => {
            if (err instanceof HttpErrorResponse && err.status === HttpStatusCode.BadRequest) {
              this.searchTextControl.setErrors({
                'invalidSearchTerms': true
              })
            }
            return of({total: 0, elem: []} as SearchResult)
          }), map(searchResult => ({elem: this.reportService.translateBackupElement(searchResult.elem), total: searchResult.total} as SearchResult))), of(({elem: [], total: 0} as SearchResult)));
        }), tap(() => this.loading = false)).subscribe(searchResult => {
          if(searchResult.total < this.PAGE_SIZE) {
            this.pagingEnd = true;
          }

          if(this.pageSubject.value === 0) {
            this.dataSource.data = searchResult.elem || []
          } else {
            this.dataSource.data = [...this.dataSource.data, ...(searchResult.elem || [])];
          }
      });
  }

  ngOnDestroy() {
    this.searchResultsSubscription.unsubscribe();
  }

  ngAfterViewInit() {
    window.setTimeout(() => this.inputField.focus());
    this.dataSource.sort = this.sort;
  }

  onTableScroll(event: any): void {
    const scrollTop = event.target.scrollTop;
    const scrollHeight = event.target.scrollHeight;
    const offsetHeight = event.target.offsetHeight;

    if (scrollHeight - (scrollTop + offsetHeight) < 50 && !this.loading && !this.pagingEnd) {
      this.pageSubject.next(this.pageSubject.value + 1);
    }
  }

  navigateToNode(searchElement: SearchElement) {
    this.activeSearchElement = searchElement.id;
    this.reportService.navigate(searchElement);
  }

  onFoldedChange(folded: boolean) {
    this.folded = folded;
  }

}
